import { SimEventKind } from "@highmarch/domain";
import type { Enemy, SimEvent } from "@highmarch/domain";
import type { EnemyStatusMarks } from "../../../../art/vfx.js";

/**
 * The pure parts of the element towers' presentation (owner report 2026-09-23: "the frost tower just seems to flash"):
 * how an enemy's sim status maps onto the marks drawn on it, how much bigger a shot is per tier, and when the beacon's
 * pulse reaches a tower. No rules live here — the sim has decided who is slowed, burning or shredded, and which towers
 * the aura buffs; this only turns that state into sizes and times. Kept free of three.js so it is unit-tested.
 */

/** Sim ticks over which a wearing-off slow fades its frost crust (the last 0.3 s at 20 Hz), so it does not pop off. */
export const FROST_FADE_TICKS = 6;
/** A slow at (or within float error of) 100 % holds the enemy still: Mythic "Deep Freeze". Drawn as frozen solid. */
export const FROZEN_FRACTION = 0.999;

/** A fresh, empty set of marks (one per enemy view, reused every frame). */
export function emptyMarks(seed = 0): EnemyStatusMarks {
  return { frost: 0, frozen: false, burn: 0, shred: 0, seed, time: 0 };
}

/**
 * The marks `e` wears, from its sim state: `frost` the slow's strength (1, fading over its last FROST_FADE_TICKS),
 * `frozen` when the slow holds it still, `burn` one per burn stack (the sim keeps one per Fire tower, up to its max),
 * `shred` the Poison tower's shred stacks. Writes into `out` (no allocation) and returns it.
 */
export function statusMarks(e: Pick<Enemy, "slowTicksLeft" | "slowFraction" | "burnStackTicks" | "shredStacks">, out: EnemyStatusMarks): EnemyStatusMarks {
  const slowed = e.slowTicksLeft > 0;
  out.frost = slowed ? Math.min(1, e.slowTicksLeft / FROST_FADE_TICKS) : 0;
  out.frozen = slowed && e.slowFraction.toNumber() >= FROZEN_FRACTION;
  out.burn = e.burnStackTicks.length;
  out.shred = e.shredStacks;
  return out;
}

/** True when there is anything to draw. */
export function hasMarks(m: EnemyStatusMarks): boolean { return m.frost > 0 || m.burn > 0 || m.shred > 0; }

/**
 * Element shot size by tier (towers.json tiers 1–3): the crystal's charge, the round, its trail and its impact all
 * scale by this, so an upgrade reads at a glance. Frost, fire and poison change nothing but range and damage between
 * tiers; the visuals grow with that, they do not add an area the sim does not have. Kept modest: the round's model
 * grows with it too, and a tier-3 fireball at 1.36× (2.2× its modelled size) read as a flat pale blob.
 */
export const ELEMENT_TIER_SCALE: readonly number[] = [1, 1.12, 1.24];
export function tierScale(tier: number): number {
  const i = Math.max(1, Math.min(ELEMENT_TIER_SCALE.length, Math.round(tier))) - 1;
  return ELEMENT_TIER_SCALE[i];
}

/**
 * The lightning tower's `fire` clip flashes its crystal on Blender frame 8 of a 30 fps clip (art/hq_anim.py
 * "lightning"), 0.267 s in; in RELEASE_FPS (30) frames like views/projectiles.ts TOWER_SHOT. The arcs
 * leave then, not 0.22 s after the event as the tesla default had it.
 */
export const LIGHTNING_RELEASE_FRAME = 8;

/**
 * The beacon's aura (vfx.js auraRing): a standing ring of ground motes at its range, turning at `spin` rad/s, and every
 * `pulseEvery` s a pulse ring running out from the tower to the range in `pulseLife` s.
 */
export const BEACON = {
  pulseEvery: 2.4,
  pulseLife: 1.1,
  /** Standing ring alpha by tier (1–3), throbbing ±`throb` of it at `throbHz`. */
  alpha: [0.9, 1.05, 1.2] as readonly number[],
  throb: 0.25,
  throbHz: 0.5,
  /** Dashes on the standing ring per cell of radius, the ring's width and its node size (world units) by tier. */
  motesPerCell: 16,
  thickness: [0.1, 0.12, 0.14] as readonly number[],
  size: [0.3, 0.34, 0.4] as readonly number[],
  spin: 0.12,
  /** The pulse ring: its dashes, width, node size and starting alpha. */
  pulseMotes: 40,
  pulseThickness: 0.16,
  pulseSize: 0.3,
  pulseAlpha: 1.4,
} as const;

/** Index into a by-tier table, clamped to it. */
export function byTier(table: readonly number[], tier: number): number {
  return table[Math.max(0, Math.min(table.length - 1, Math.round(tier) - 1))];
}

/** The standing aura's alpha at `time` s for a beacon of `tier`; steady under reduced effects. */
export function beaconAlpha(tier: number, time: number, reduced = false): number {
  const a = byTier(BEACON.alpha, tier);
  return reduced ? a : a * (1 - BEACON.throb + BEACON.throb * Math.sin(time * BEACON.throbHz * 2 * Math.PI));
}

/** The pulse's front at `age` s: eases out from the tower to `radius` over `life` s. */
export function pulseRadius(age: number, radius: number, life: number): number {
  const t = Math.max(0, Math.min(1, age / life));
  return radius * (1 - (1 - t) * (1 - t));
}
/** Seconds after a pulse starts at which its front reaches `distance` (the inverse of `pulseRadius`). */
export function pulseArrival(distance: number, radius: number, life: number): number {
  if (!(radius > 0)) return 0;
  const u = Math.max(0, Math.min(1, distance / radius));
  return life * (1 - Math.sqrt(1 - u));
}

/** Which element's shot has landed on an enemy (bits of `StatusGate`). */
export const StatusBit = { Frost: 1, Burn: 2, Shred: 4 } as const;
export type StatusBit = (typeof StatusBit)[keyof typeof StatusBit];
/** The status a tower's shot brings, by tower id (the rounds that fly; lightning and beacon bring none). */
export const STATUS_OF_TOWER: Readonly<Record<string, StatusBit>> = { frost: StatusBit.Frost, fire: StatusBit.Burn, poison: StatusBit.Shred };
/**
 * Seconds of the effects clock (the clock the rounds fly on) a status may wait for its round to land before it shows
 * anyway (a round dropped by the pool cap, a status from a Mythic): longer than any element round's release plus a
 * full-range flight. It was 30 sim ticks, but the ticks come faster at ×2 and ×3 while the rounds fly in real time, so
 * at ×3 a burn mark showed at 0.5 s, before its fireball landed (GUIDE_2026-09-24 follow-up).
 */
export const GATE_MAX_SECONDS = 1.5;

/**
 * The sim applies a slow, a burn or a shred the tick the tower fires; the round that brings it is still in the air
 * for ~0.4–0.9 s (release frame + flight). Drawn straight from the sim, an enemy went blue before the frost shard
 * left the crystal. The router marks a status as landed when its round hits (`land`); the enemy view shows a status
 * once it has landed, or after `GATE_MAX_TICKS` of waiting, and forgets it when the sim ends it (`clear`).
 */
export class StatusGate {
  private readonly landed = new Map<number, number>();
  private clock = 0;
  /** The effects clock, in seconds (EffectsView.update advances it with the rounds' own dt). */
  get now(): number { return this.clock; }
  advance(dt: number): void { this.clock += Math.max(0, dt); }
  land(enemyId: number, bit: StatusBit): void { this.landed.set(enemyId, (this.landed.get(enemyId) ?? 0) | bit); }
  has(enemyId: number, bit: StatusBit): boolean { return ((this.landed.get(enemyId) ?? 0) & bit) !== 0; }
  clear(enemyId: number, bit: StatusBit): void {
    const m = this.landed.get(enemyId);
    if (m === undefined) return;
    if ((m & ~bit) === 0) this.landed.delete(enemyId); else this.landed.set(enemyId, m & ~bit);
  }
  forget(enemyId: number): void { this.landed.delete(enemyId); }
  reset(): void { this.landed.clear(); this.clock = 0; }
  get size(): number { return this.landed.size; }
}
/** The one gate the level's router writes and its enemy view reads (both live and die with the level). */
export const statusGate = new StatusGate();

/** A `waits` slot with no wait running. */
export const NOT_WAITING = NaN;

/**
 * One status through the gate, per sim tick: `on` is the sim's state; `waits[i]` the gate time (`StatusGate.now`) the
 * status began waiting, or NOT_WAITING (the caller keeps one small array per enemy; updated in place, no
 * allocation). Returns whether to show it.
 */
export function gateStep(gate: StatusGate, enemyId: number, bit: StatusBit, on: boolean, waits: number[], i: number): boolean {
  if (!on) { gate.clear(enemyId, bit); waits[i] = NOT_WAITING; return false; }
  if (gate.has(enemyId, bit)) { waits[i] = NOT_WAITING; return true; }
  if (Number.isNaN(waits[i])) waits[i] = gate.now;
  return gate.now - waits[i] > GATE_MAX_SECONDS;
}

/**
 * The sim resolves a tower's damage the tick it fires (combat.ts fireTowers), but its round is drawn leaving the
 * muzzle on the clip's release frame and flying for a while after: archer ~0.25 s plus flight, the fireball ~1 s. Shown
 * straight from the sim, the hit flash, the flinch, the `hit` sound, the "+N" and the death all came before the round
 * (owner report 2026-09-24: the fire tower's pale flash at the target before the fireball lands). `ImpactGate`
 * generalises `StatusGate` to that presentation: the views queue what a shot's damage and kills should show (`wait`),
 * and the router releases it when the round lands (`land`, a projectile's `onImpact` / a pierce's pass), when the arc
 * reaches the hop (`landIn`), or by a deadline (`expect`) sized to the flight, so a round dropped by the pool cap still
 * shows its hit on time. Presentation only: the sim is never delayed or read differently.
 *
 * Keys are one tower's shot of one tick (`shotKey`): a tower fires at most once a tick, so a key is one round (or one
 * volley: the cannon's splash, the ballista's pierce, the chain's hops), and several rounds of the same tower in flight
 * stay apart. A key nobody claims by the end of the router's tick (a hitscan zap, an unknown tower) lands then (`settle`).
 * Time is the effects clock (seconds, the router's `update`), the clock the rounds fly on: not scaled by game speed, and
 * slowed with them by a hit-stop.
 */
export const IMPACT = {
  /** A wait nobody claims or lands lets go after this long anyway (longer than any round's release plus flight). */
  maxSeconds: 1.5,
  /** Slack on a round's expected landing time, so the deadline only catches a round that never flew. */
  graceSeconds: 0.2,
} as const;

/** Tower ids stay below this (the views already pack tower × 65536 + enemy). */
const TOWER_KEY_SPAN = 65536;
/** The key of the round `towerId` fired on `tick`. */
export function shotKey(tick: number, towerId: number): number { return tick * TOWER_KEY_SPAN + towerId; }

/**
 * For each of one tick's events, the tower whose round of this tick brought it (0: none — show it now). Only
 * `EnemyDamaged` and `EnemyKilled` are routed: their `b` is the source tower, and a tower's round, splash, pierce and
 * chain all follow its `TowerFired` in the buffer (combat.ts fireTowers); the pet's hit after its PetBit is PET_ROUND. A
 * burn's damage-over-time ticks, a troop's blow and anything before the tower fires come first in the step
 * (LevelSim.step), so they stay immediate.
 */
export function roundOf(events: readonly SimEvent[]): number[] {
  const fired = new Set<number>();
  /** Raiders the pet struck this tick: "bit" until its hit's EnemyDamaged, then "hit" until a kill may follow. */
  const bitten = new Map<number, "bit" | "hit">();
  return events.map((ev) => {
    if (ev.kind === SimEventKind.TowerFired) { fired.add(ev.a); return 0; }
    if (ev.kind === SimEventKind.PetBit) { bitten.set(ev.a, "bit"); return 0; }
    if (ev.kind !== SimEventKind.EnemyDamaged && ev.kind !== SimEventKind.EnemyKilled) return 0;
    if (ev.b > 0) return fired.has(ev.b) ? ev.b : 0;
    const pet = bitten.get(ev.a);
    if (pet === undefined) return 0;
    // the pet's own hit (no tower: b = 0) and the kill straight after it; a later troop blow on it stays immediate
    if (ev.kind === SimEventKind.EnemyDamaged && pet === "bit") { bitten.set(ev.a, "hit"); return PET_ROUND; }
    bitten.delete(ev.a);
    return ev.kind === SimEventKind.EnemyKilled && pet === "hit" ? PET_ROUND : 0;
  });
}

/**
 * The pet's attack (PetBit) as a round: the sim applies its damage the tick it strikes, but the pet is drawn running to
 * the raider (or spitting at it) first (views/petStrike.ts), so its hit, number and kill wait for the blow like a
 * tower's round (PetView claims and lands `shotKey(tick, PET_ROUND)`). The top of the tower-id span: no tower has it.
 */
export const PET_ROUND = TOWER_KEY_SPAN - 1;

interface GatedShot {
  /** When everything still waiting on this shot shows, whatever happens to the round. */
  deadline: number;
  /** The router has said how this shot lands (a round, an arc, a strike); unclaimed shots land at `settle`. */
  claimed: boolean;
  waits: { enemy: number; fn: () => void }[];
  /** Per-victim landing times (the chain's hops), when the shot lands on its victims one by one. */
  landAt: Map<number, number> | null;
}

export class ImpactGate {
  private now = 0;
  private readonly shots = new Map<number, GatedShot>();

  /** Shows `fn` when shot `key` lands on `enemy` (or its deadline passes). */
  wait(key: number, enemy: number, fn: () => void): void {
    let s = this.shots.get(key);
    if (!s) { s = { deadline: this.now + IMPACT.maxSeconds, claimed: false, waits: [], landAt: null }; this.shots.set(key, s); }
    s.waits.push({ enemy, fn });
  }

  /** The router flies a round for `key`: it lands (`land`) within `seconds`, or its waits show then anyway. */
  expect(key: number, seconds: number): void {
    const s = this.shots.get(key);
    if (!s) return;                                     // nothing waits on it
    s.claimed = true;
    s.deadline = this.now + Math.min(IMPACT.maxSeconds, Math.max(0, seconds));
  }

  /** Shot `key` lands on `enemy` in `seconds` (a chain hop the arc reaches then). Claims the shot. */
  landIn(key: number, enemy: number, seconds: number): void {
    const s = this.shots.get(key);
    if (!s) return;
    s.claimed = true;
    (s.landAt ??= new Map()).set(enemy, this.now + Math.max(0, seconds));
  }

  /** Shot `key` has landed: on `enemy` alone (a piercing bolt passing it), or on everything it hit. */
  land(key: number, enemy?: number): void {
    const s = this.shots.get(key);
    if (!s) return;
    const due = enemy === undefined ? s.waits : s.waits.filter((w) => w.enemy === enemy);
    s.waits = enemy === undefined ? [] : s.waits.filter((w) => w.enemy !== enemy);
    if (s.waits.length === 0) this.shots.delete(key);
    for (const w of due) w.fn();
  }

  /** End of the router's tick: shots nobody claimed (hitscan, or a shot the router could not draw) show now. */
  settle(): void {
    for (const [key, s] of [...this.shots]) if (!s.claimed) this.land(key);
  }

  /** Advances the effects clock and shows what is due. */
  update(dt: number): void {
    this.now += dt;
    for (const [key, s] of [...this.shots]) {
      if (s.deadline <= this.now) { this.land(key); continue; }
      if (s.landAt) for (const [enemy, at] of s.landAt) if (at <= this.now) { s.landAt.delete(enemy); this.land(key, enemy); }
    }
  }

  /** Drops everything (leaving the level): nothing queued may run into the next one. */
  reset(): void { this.shots.clear(); this.now = 0; }
  /** Shots with something still waiting. */
  get size(): number { return this.shots.size; }
}
/** The one gate the level's router lands and its enemy view and sound routing wait on (they live and die with the level). */
export const impactGate = new ImpactGate();
